import { useEffect, useState, useCallback } from 'react';
import { useDebounce } from 'use-debounce';
import { isoUTCToLocal } from '../../../utils/formatDate';

export interface FormData {
    name: string,
    description: string,
    code: string,
    startDate: string,
    endDate: string,
    showAnswersDate: string,
    isExclusive: boolean,
}

type UseExamCodeFormProps = {
    initialFormData: FormData;
    debounceDelay?: number;
};

const normalizeFormData = (data: FormData): FormData => ({
    name: data.name ?? '',
    description: data.description ?? '',
    code: data.code ?? '',
    startDate: isoUTCToLocal(data.startDate),
    endDate: isoUTCToLocal(data.endDate),
    showAnswersDate: isoUTCToLocal(data.showAnswersDate),
    isExclusive: data.isExclusive ?? false,
});


export function useExamCodeForm({ initialFormData, debounceDelay = 300 }: UseExamCodeFormProps) {
    // Normalize ONCE for first render
    const normalizedInitial = normalizeFormData(initialFormData);

    // Form data state from the api call
    const [formData, setFormData] = useState<FormData>(normalizedInitial);

    // Raw form debouncing and data or the dirty form data once the user
    // filled out information in textfields
    const [rawFormData, setRawFormData] = useState<FormData>(normalizedInitial);
    const [debouncedFormData] = useDebounce(rawFormData, debounceDelay);

    // Touch fields recording to handle error textfield validation
    const [touchedFields, setTouchedFields] = useState<Record<keyof FormData, boolean>>({
        name: false,
        description: false,
        code: false,
        startDate: false,
        endDate: false,
        showAnswersDate: false,
        isExclusive: false
    });

    useEffect(() => {
        const normalized = normalizeFormData(initialFormData);
        setRawFormData(normalized);
        setFormData(normalized);
        setTouchedFields({
            name: false,
            description: false,
            code: false,
            startDate: false,
            endDate: false,
            showAnswersDate: false,
            isExclusive: false,
        });
    }, [initialFormData]);

    // Debouncing mechanism for form state (create / edit)
    useEffect(() => {
        setFormData(debouncedFormData);
    }, [debouncedFormData]);

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const { name, value, type, checked } = e.target as HTMLInputElement;
            setRawFormData((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        },
        [],
    );

    const handleBlur = useCallback(
        (field: keyof FormData) => {
            setTouchedFields((prev) => ({ ...prev, [field]: true }));
        },
        [],
    );

    const resetForm = useCallback(
        (newInitialData?: FormData) => {
            const normalized = normalizeFormData(
                newInitialData ?? initialFormData
            );

            setRawFormData(normalized);
            setFormData(normalized);
            setTouchedFields({
                name: false,
                description: false,
                code: false,
                startDate: false,
                endDate: false,
                showAnswersDate: false,
                isExclusive: false,
            });
        },
        [initialFormData]
    );

    const isFormValid = useCallback(() => {
        return (
            !!formData.name?.trim() &&
            !!formData.description?.trim() &&
            !!formData.code?.trim() &&
            !!formData.startDate?.trim() &&
            !!formData.endDate?.trim() &&
            !!formData.showAnswersDate?.trim() &&
            formData.isExclusive !== undefined
        );
    }, [formData]);

    return {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid,
    };
}
